import { useCallback } from "react";

import { useStore } from "react-redux";

import { useUser } from "../../entities/users/hooks";
import { actions } from "../../entities/slices/user.slice";
import { selectUser } from "../../entities/users/selectors";
import { useAppDispatch } from "../../entities/utils/useAppDispatch.hook";

import { useSetPreferredLanguage } from "./requests";

import type { RootState } from "../../entities/store";

interface SetPreferredLanguageOptions {
  /** Called after a failed save, once the user has been rolled back. */
  onError?: () => void;
}

/**
 * The language picked in the app's language picker is the user's preferred
 * language. Saving updates the user optimistically and rolls back on failure.
 */
export const usePreferredLanguage = () => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { setPreferredLanguage: save, loading: isSaving } =
    useSetPreferredLanguage();

  const setPreferredLanguage = useCallback(
    (
      preferredLanguage: string,
      { onError }: SetPreferredLanguageOptions = {},
    ) => {
      if (!user || user.preferred_language === preferredLanguage) {
        return;
      }
      save(
        { preferred_language: preferredLanguage },
        {
          optimistic: {
            update: () => {
              dispatch(
                actions.upsert({
                  ...user,
                  preferred_language: preferredLanguage,
                }),
              );
            },
            rollback: () => {
              // The save can fail after a profile switch or sign-out
              const signedInUser = selectUser(store.getState());
              if (signedInUser?.id !== user.id) {
                return;
              }
              dispatch(
                actions.upsert({
                  ...signedInUser,
                  preferred_language: user.preferred_language,
                }),
              );
            },
          },
          onError,
        },
      );
    },
    [dispatch, save, store, user],
  );

  return {
    preferredLanguage: user?.preferred_language ?? null,
    isSaving,
    setPreferredLanguage,
  };
};
